// utils/currency/registry.js
//
// The currency registry: ISO 4217 codes, their minor-unit exponents, and the
// symbols the formatter prints for the `currency` element type
// (docs/plans/currency.md in keepr-web).
//
// DEPENDENCY-FREE AND PLATFORM-FREE ON PURPOSE. Every file in utils/currency/
// is vendored verbatim into keepr-web (`src/utils/currency/vendored/`) and
// kept in lock-step by the shared test-vector file next to it
// (`vectors.json`), which both repos' suites drive. Nothing here may
// require() anything outside this folder -- no Mongo, no Hapi, no Boom, no
// Intl-dependent behaviour (see format.js for why Intl is not used).
//
// Shape:
//
//   currency: { code, name, exponent, symbol, narrow, fund, aliases: [...] }
//
//   code      the ISO 4217 alphabetic code, upper case -- the stored id.
//   exponent  the ISO 4217 minor-unit exponent: USD 2, JPY 0, BHD 3, CLF 4.
//             A stored amount is an INTEGER count of minor units, so an
//             exponent is a STORAGE FORMAT, exactly like a unit factor in
//             utils/units/registry.js: `{ amount: 1250, currency: 'USD' }`
//             means $12.50 only because USD's exponent is 2. Changing an
//             exponent, or removing a code, silently re-values every amount
//             already saved in it. Both are data migrations, never edits;
//             test/currency.test.js pins every exponent twice (here and in the
//             shared vectors).
//   symbol    what an en-US reader recognises unambiguously: "$" for USD,
//             "CA$" for CAD, "€", "£", "¥" for JPY, "CN¥" for CNY, and the
//             code itself for the long tail ("CHF", "SEK").
//   narrow    the local glyph, ambiguous across currencies: "$" for USD, CAD,
//             AUD, MXN...; "kr" for SEK, NOK, DKK, ISK. format.js prints it
//             only when no OTHER currency the element allows shares it -- a
//             CAD-only element reads "$12.50", a USD + CAD one "CA$12.50".
//   fund      true for the ISO "fund" codes (BOV, CHE, CHW, CLF, COU, MXV,
//             USN, UYI, UYW): real codes an element may allow, listed after
//             the ordinary currencies by pickers.
//   aliases   extra spellings the parser accepts ("US$", "CAN$"), lowercase. Never a
//             glyph another currency prints: "C$" is the cordoba's, so it is
//             not an alias of CAD.
//
// The list is ISO 4217 List One as circulating in 2026, minus the codes ISO
// has withdrawn (HRK, SLL, ZWL, ANG, BGN, CUC) and the precious-metal and
// testing codes that have no minor unit (XAU, XAG, XPT, XPD, XDR, XSU, XUA,
// XBA-XBD, XTS, XXX). A code added later is safe; a code removed is not (see
// `exponent` above), so a withdrawn currency that ever gains stored data
// stays in this file.

'use strict';

// [code, exponent, name, symbol, narrow, extras]
// extras: { fund?: true, aliases?: [...] }
const ROWS = [
	['AED', 2, 'UAE dirham', 'AED', 'AED'],
	['AFN', 2, 'Afghan afghani', 'AFN', '؋'],
	['ALL', 2, 'Albanian lek', 'ALL', 'ALL'],
	['AMD', 2, 'Armenian dram', 'AMD', '֏'],
	['AOA', 2, 'Angolan kwanza', 'AOA', 'Kz'],
	['ARS', 2, 'Argentine peso', 'ARS', '$'],
	['AUD', 2, 'Australian dollar', 'A$', '$', { aliases: ['au$'] }],
	['AWG', 2, 'Aruban florin', 'AWG', 'AWG'],
	['AZN', 2, 'Azerbaijani manat', 'AZN', '₼'],
	['BAM', 2, 'Bosnia and Herzegovina convertible mark', 'BAM', 'KM'],
	['BBD', 2, 'Barbadian dollar', 'BBD', '$'],
	['BDT', 2, 'Bangladeshi taka', 'BDT', '৳'],
	['BHD', 3, 'Bahraini dinar', 'BHD', 'BHD'],
	['BIF', 0, 'Burundian franc', 'BIF', 'BIF'],
	['BMD', 2, 'Bermudian dollar', 'BMD', '$'],
	['BND', 2, 'Brunei dollar', 'BND', '$'],
	['BOB', 2, 'Bolivian boliviano', 'BOB', 'Bs'],
	['BOV', 2, 'Bolivian Mvdol', 'BOV', 'BOV', { fund: true }],
	['BRL', 2, 'Brazilian real', 'R$', 'R$'],
	['BSD', 2, 'Bahamian dollar', 'BSD', '$'],
	['BTN', 2, 'Bhutanese ngultrum', 'BTN', 'BTN'],
	['BWP', 2, 'Botswana pula', 'BWP', 'P'],
	['BYN', 2, 'Belarusian ruble', 'BYN', 'BYN'],
	['BZD', 2, 'Belize dollar', 'BZD', '$'],
	['CAD', 2, 'Canadian dollar', 'CA$', '$', { aliases: ['can$', 'cad$'] }],
	['CDF', 2, 'Congolese franc', 'CDF', 'CDF'],
	['CHE', 2, 'WIR euro', 'CHE', 'CHE', { fund: true }],
	['CHF', 2, 'Swiss franc', 'CHF', 'CHF'],
	['CHW', 2, 'WIR franc', 'CHW', 'CHW', { fund: true }],
	['CLF', 4, 'Chilean unit of account (UF)', 'CLF', 'CLF', { fund: true }],
	['CLP', 0, 'Chilean peso', 'CLP', '$'],
	['CNY', 2, 'Chinese yuan', 'CN¥', '¥', { aliases: ['rmb'] }],
	['COP', 2, 'Colombian peso', 'COP', '$'],
	['COU', 2, 'Colombian unit of real value', 'COU', 'COU', { fund: true }],
	['CRC', 2, 'Costa Rican colón', 'CRC', '₡'],
	['CUP', 2, 'Cuban peso', 'CUP', '$'],
	['CVE', 2, 'Cape Verdean escudo', 'CVE', 'CVE'],
	['CZK', 2, 'Czech koruna', 'CZK', 'Kč'],
	['DJF', 0, 'Djiboutian franc', 'DJF', 'DJF'],
	['DKK', 2, 'Danish krone', 'DKK', 'kr'],
	['DOP', 2, 'Dominican peso', 'DOP', '$'],
	['DZD', 2, 'Algerian dinar', 'DZD', 'DZD'],
	['EGP', 2, 'Egyptian pound', 'EGP', 'E£'],
	['ERN', 2, 'Eritrean nakfa', 'ERN', 'ERN'],
	['ETB', 2, 'Ethiopian birr', 'ETB', 'ETB'],
	['EUR', 2, 'Euro', '€', '€'],
	['FJD', 2, 'Fijian dollar', 'FJD', '$'],
	['FKP', 2, 'Falkland Islands pound', 'FKP', '£'],
	['GBP', 2, 'British pound', '£', '£'],
	['GEL', 2, 'Georgian lari', 'GEL', '₾'],
	['GHS', 2, 'Ghanaian cedi', 'GHS', 'GH₵'],
	['GIP', 2, 'Gibraltar pound', 'GIP', '£'],
	['GMD', 2, 'Gambian dalasi', 'GMD', 'GMD'],
	['GNF', 0, 'Guinean franc', 'GNF', 'FG'],
	['GTQ', 2, 'Guatemalan quetzal', 'GTQ', 'Q'],
	['GYD', 2, 'Guyanese dollar', 'GYD', '$'],
	['HKD', 2, 'Hong Kong dollar', 'HK$', '$'],
	['HNL', 2, 'Honduran lempira', 'HNL', 'L'],
	['HTG', 2, 'Haitian gourde', 'HTG', 'HTG'],
	['HUF', 2, 'Hungarian forint', 'HUF', 'Ft'],
	['IDR', 2, 'Indonesian rupiah', 'IDR', 'Rp'],
	['ILS', 2, 'Israeli new shekel', '₪', '₪'],
	['INR', 2, 'Indian rupee', '₹', '₹'],
	['IQD', 3, 'Iraqi dinar', 'IQD', 'IQD'],
	['IRR', 2, 'Iranian rial', 'IRR', 'IRR'],
	['ISK', 0, 'Icelandic króna', 'ISK', 'kr'],
	['JMD', 2, 'Jamaican dollar', 'JMD', '$'],
	['JOD', 3, 'Jordanian dinar', 'JOD', 'JOD'],
	['JPY', 0, 'Japanese yen', '¥', '¥'],
	['KES', 2, 'Kenyan shilling', 'KES', 'KES'],
	['KGS', 2, 'Kyrgyzstani som', 'KGS', 'KGS'],
	['KHR', 2, 'Cambodian riel', 'KHR', '៛'],
	['KMF', 0, 'Comorian franc', 'KMF', 'CF'],
	['KPW', 2, 'North Korean won', 'KPW', '₩'],
	['KRW', 0, 'South Korean won', '₩', '₩'],
	['KWD', 3, 'Kuwaiti dinar', 'KWD', 'KWD'],
	['KYD', 2, 'Cayman Islands dollar', 'KYD', '$'],
	['KZT', 2, 'Kazakhstani tenge', 'KZT', '₸'],
	['LAK', 2, 'Lao kip', 'LAK', '₭'],
	['LBP', 2, 'Lebanese pound', 'LBP', 'L£'],
	['LKR', 2, 'Sri Lankan rupee', 'LKR', 'Rs'],
	['LRD', 2, 'Liberian dollar', 'LRD', '$'],
	['LSL', 2, 'Lesotho loti', 'LSL', 'LSL'],
	['LYD', 3, 'Libyan dinar', 'LYD', 'LYD'],
	['MAD', 2, 'Moroccan dirham', 'MAD', 'MAD'],
	['MDL', 2, 'Moldovan leu', 'MDL', 'MDL'],
	['MGA', 2, 'Malagasy ariary', 'MGA', 'Ar'],
	['MKD', 2, 'Macedonian denar', 'MKD', 'MKD'],
	['MMK', 2, 'Myanmar kyat', 'MMK', 'K'],
	['MNT', 2, 'Mongolian tögrög', 'MNT', '₮'],
	['MOP', 2, 'Macanese pataca', 'MOP', 'MOP'],
	['MRU', 2, 'Mauritanian ouguiya', 'MRU', 'MRU'],
	['MUR', 2, 'Mauritian rupee', 'MUR', 'Rs'],
	['MVR', 2, 'Maldivian rufiyaa', 'MVR', 'MVR'],
	['MWK', 2, 'Malawian kwacha', 'MWK', 'MWK'],
	['MXN', 2, 'Mexican peso', 'MX$', '$'],
	['MXV', 2, 'Mexican investment unit', 'MXV', 'MXV', { fund: true }],
	['MYR', 2, 'Malaysian ringgit', 'MYR', 'RM'],
	['MZN', 2, 'Mozambican metical', 'MZN', 'MZN'],
	['NAD', 2, 'Namibian dollar', 'NAD', '$'],
	['NGN', 2, 'Nigerian naira', 'NGN', '₦'],
	['NIO', 2, 'Nicaraguan córdoba', 'NIO', 'C$'],
	['NOK', 2, 'Norwegian krone', 'NOK', 'kr'],
	['NPR', 2, 'Nepalese rupee', 'NPR', 'Rs'],
	['NZD', 2, 'New Zealand dollar', 'NZ$', '$'],
	['OMR', 3, 'Omani rial', 'OMR', 'OMR'],
	['PAB', 2, 'Panamanian balboa', 'PAB', 'PAB'],
	['PEN', 2, 'Peruvian sol', 'PEN', 'PEN'],
	['PGK', 2, 'Papua New Guinean kina', 'PGK', 'PGK'],
	['PHP', 2, 'Philippine peso', '₱', '₱'],
	['PKR', 2, 'Pakistani rupee', 'PKR', 'Rs'],
	['PLN', 2, 'Polish złoty', 'PLN', 'zł'],
	['PYG', 0, 'Paraguayan guaraní', 'PYG', '₲'],
	['QAR', 2, 'Qatari riyal', 'QAR', 'QAR'],
	['RON', 2, 'Romanian leu', 'RON', 'lei'],
	['RSD', 2, 'Serbian dinar', 'RSD', 'RSD'],
	['RUB', 2, 'Russian ruble', 'RUB', '₽'],
	['RWF', 0, 'Rwandan franc', 'RWF', 'RF'],
	['SAR', 2, 'Saudi riyal', 'SAR', 'SAR'],
	['SBD', 2, 'Solomon Islands dollar', 'SBD', '$'],
	['SCR', 2, 'Seychellois rupee', 'SCR', 'SCR'],
	['SDG', 2, 'Sudanese pound', 'SDG', 'SDG'],
	['SEK', 2, 'Swedish krona', 'SEK', 'kr'],
	['SGD', 2, 'Singapore dollar', 'SGD', '$'],
	['SHP', 2, 'Saint Helena pound', 'SHP', '£'],
	['SLE', 2, 'Sierra Leonean leone', 'SLE', 'SLE'],
	['SOS', 2, 'Somali shilling', 'SOS', 'SOS'],
	['SRD', 2, 'Surinamese dollar', 'SRD', '$'],
	['SSP', 2, 'South Sudanese pound', 'SSP', '£'],
	['STN', 2, 'São Tomé and Príncipe dobra', 'STN', 'Db'],
	['SVC', 2, 'Salvadoran colón', 'SVC', 'SVC'],
	['SYP', 2, 'Syrian pound', 'SYP', '£'],
	['SZL', 2, 'Swazi lilangeni', 'SZL', 'SZL'],
	['THB', 2, 'Thai baht', 'THB', '฿'],
	['TJS', 2, 'Tajikistani somoni', 'TJS', 'TJS'],
	['TMT', 2, 'Turkmenistan manat', 'TMT', 'TMT'],
	['TND', 3, 'Tunisian dinar', 'TND', 'TND'],
	['TOP', 2, 'Tongan paʻanga', 'TOP', 'T$'],
	['TRY', 2, 'Turkish lira', 'TRY', '₺'],
	['TTD', 2, 'Trinidad and Tobago dollar', 'TTD', '$'],
	['TWD', 2, 'New Taiwan dollar', 'NT$', '$'],
	['TZS', 2, 'Tanzanian shilling', 'TZS', 'TZS'],
	['UAH', 2, 'Ukrainian hryvnia', 'UAH', '₴'],
	['UGX', 0, 'Ugandan shilling', 'UGX', 'UGX'],
	['USD', 2, 'US dollar', '$', '$', { aliases: ['us$', 'usd$'] }],
	['USN', 2, 'US dollar (next day)', 'USN', 'USN', { fund: true }],
	['UYI', 0, 'Uruguay peso in indexed units', 'UYI', 'UYI', { fund: true }],
	['UYU', 2, 'Uruguayan peso', 'UYU', '$'],
	['UYW', 4, 'Uruguay nominal wage index unit', 'UYW', 'UYW', { fund: true }],
	['UZS', 2, 'Uzbekistani som', 'UZS', 'UZS'],
	['VED', 2, 'Venezuelan digital bolívar', 'VED', 'VED'],
	['VES', 2, 'Venezuelan bolívar', 'VES', 'VES'],
	['VND', 0, 'Vietnamese đồng', '₫', '₫'],
	['VUV', 0, 'Vanuatu vatu', 'VUV', 'VUV'],
	['WST', 2, 'Samoan tālā', 'WST', 'WST'],
	['XAF', 0, 'Central African CFA franc', 'FCFA', 'FCFA'],
	['XCD', 2, 'East Caribbean dollar', 'EC$', '$'],
	['XCG', 2, 'Caribbean guilder', 'XCG', 'XCG'],
	['XOF', 0, 'West African CFA franc', 'F CFA', 'F CFA'],
	['XPF', 0, 'CFP franc', 'CFPF', 'CFPF'],
	['YER', 2, 'Yemeni rial', 'YER', 'YER'],
	['ZAR', 2, 'South African rand', 'ZAR', 'R'],
	['ZMW', 2, 'Zambian kwacha', 'ZMW', 'ZK'],
	['ZWG', 2, 'Zimbabwe gold', 'ZWG', 'ZWG'],
];

// The largest amount a stored integer may hold without losing a unit: past
// Number.MAX_SAFE_INTEGER two different amounts share one double, so a write
// above it is refused (normalize.js, `range`) rather than silently rounded.
const MAX_AMOUNT = 9007199254740991;

function freezeDeep(obj) {
	Object.values(obj).forEach(v => {
		if (v && typeof v === 'object' && !Object.isFrozen(v)) freezeDeep(v);
	});
	return Object.freeze(obj);
}

const CURRENCIES = {};
for (const row of ROWS) {
	const [code, exponent, name, symbol, narrow, extras] = row;
	const more = extras || {};
	CURRENCIES[code] = {
		code,
		name,
		exponent,
		symbol,
		narrow,
		fund: more.fund === true,
		aliases: (more.aliases || []).slice(),
	};
}
freezeDeep(CURRENCIES);

/** Every currency, ordinary codes first, each group in code order. */
const CURRENCY_LIST = Object.freeze(Object.values(CURRENCIES)
	.slice()
	.sort((a, b) => (a.fund === b.fund ? (a.code < b.code ? -1 : 1) : (a.fund ? 1 : -1))));

// Case-insensitive CODE lookup plus the explicit aliases. Symbols are NOT in
// here: "$" names a dozen currencies and is resolved against the element
// (resolveCurrency), never by a global table.
const SPELLINGS = {};
for (const currency of Object.values(CURRENCIES)) {
	SPELLINGS[currency.code.toLowerCase()] = currency.code;
	for (const alias of currency.aliases) SPELLINGS[alias] = currency.code;
}
Object.freeze(SPELLINGS);

// Every currency printed with a symbol, by that symbol (exact, case-sensitive:
// "R" is the rand, "r" is nothing). A symbol shared by several currencies
// lists them all; resolveCurrency picks among them.
const BY_GLYPH = {};
function addGlyph(glyph, code) {
	if (!glyph) return;
	if (!BY_GLYPH[glyph]) BY_GLYPH[glyph] = [];
	if (BY_GLYPH[glyph].indexOf(code) === -1) BY_GLYPH[glyph].push(code);
}
for (const currency of Object.values(CURRENCIES)) {
	addGlyph(currency.symbol, currency.code);
	addGlyph(currency.narrow, currency.code);
}
freezeDeep(BY_GLYPH);

/** The currency with this ISO code (exact upper case, the stored spelling); null otherwise. */
function getCurrency(code) {
	if (typeof code !== 'string') return null;
	return Object.prototype.hasOwnProperty.call(CURRENCIES, code) ? CURRENCIES[code] : null;
}

function isCurrencyCode(code) {
	return getCurrency(code) !== null;
}

/** The minor-unit exponent of a code, or null for an unknown one. */
function exponentOf(code) {
	const currency = getCurrency(code);
	return currency ? currency.exponent : null;
}

/** The codes an element allows, known ones only, in the author's order, de-duplicated. */
function allowedCodes(options) {
	const list = options && Array.isArray(options.currencies) ? options.currencies : [];
	const out = [];
	for (const code of list) {
		if (isCurrencyCode(code) && out.indexOf(code) === -1) out.push(code);
	}
	return out;
}

/**
 * The code a bare number means for an element: `defaultCurrency` when it is a
 * known code the element allows, else the first allowed code, else null (an
 * element with no currencies has no reading for a bare number).
 */
function defaultCodeOf(options) {
	const allowed = allowedCodes(options);
	const preferred = options && typeof options.defaultCurrency === 'string' ? options.defaultCurrency : null;
	if (preferred && isCurrencyCode(preferred) && (!allowed.length || allowed.indexOf(preferred) !== -1)) return preferred;
	return allowed.length ? allowed[0] : null;
}

/**
 * Every code a spelling can name: [code] for a code in any case ("usd") or an
 * alias ("US$"); every currency printing it for a symbol ("$" -> USD, CAD,
 * AUD, ...; "CA$" -> CAD); [] for anything else.
 */
function currencyCandidates(spelling) {
	if (typeof spelling !== 'string') return [];
	const text = spelling.trim().replace(/\s+/g, ' ');
	if (!text) return [];
	const key = text.toLowerCase();
	if (Object.prototype.hasOwnProperty.call(SPELLINGS, key)) return [SPELLINGS[key]];
	return Object.prototype.hasOwnProperty.call(BY_GLYPH, text) ? BY_GLYPH[text].slice() : [];
}

/**
 * The one code a candidate list means on an element, most specific first:
 *   1. the only candidate, when there is one;
 *   2. the element's default currency, when it is a candidate;
 *   3. the first allowed currency that is a candidate, in the author's order;
 *   4. with no element context (or none of its currencies a candidate), the
 *      ONE candidate whose unambiguous `symbol` is exactly `glyph` ("$" is
 *      USD, "¥" is JPY, "£" is GBP) -- or null when even that is shared.
 */
function pickCandidate(candidates, options, glyph) {
	if (!candidates.length) return null;
	if (candidates.length === 1) return candidates[0];
	const preferred = defaultCodeOf(options);
	if (preferred && candidates.indexOf(preferred) !== -1) return preferred;
	for (const code of allowedCodes(options)) {
		if (candidates.indexOf(code) !== -1) return code;
	}
	const exact = candidates.filter(code => CURRENCIES[code].symbol === glyph);
	return exact.length === 1 ? exact[0] : null;
}

/**
 * Resolve a spelling -- a code in any case ("usd"), an alias ("US$", "CAN$"),
 * or a symbol ("$", "€", "CA$", "kr") -- to a code, or null. A symbol may
 * name several currencies, so it is resolved against the element
 * (pickCandidate). Whether the result is ALLOWED is the normaliser's
 * question, not this one's: "$5" into a EUR-only element resolves to USD and
 * is refused there, with a message naming the currency, rather than failing
 * here as unreadable.
 */
function resolveCurrency(spelling, options) {
	const glyph = typeof spelling === 'string' ? spelling.trim().replace(/\s+/g, ' ') : '';
	return pickCandidate(currencyCandidates(spelling), options, glyph);
}

/** The stored shape: a plain object with a safe-integer `amount` and a string `currency`. */
function isCurrencyValue(v) {
	return !!v
		&& typeof v === 'object'
		&& !Array.isArray(v)
		&& typeof v.amount === 'number'
		&& Number.isSafeInteger(v.amount)
		&& typeof v.currency === 'string';
}

module.exports = {
	CURRENCIES,
	CURRENCY_LIST,
	MAX_AMOUNT,
	getCurrency,
	isCurrencyCode,
	exponentOf,
	allowedCodes,
	defaultCodeOf,
	currencyCandidates,
	pickCandidate,
	resolveCurrency,
	isCurrencyValue,
};
